// /settings/danger — удаление аккаунта (30-day grace + hard wipe).
// Тактильный редизайн «Картотека» — спокойный, ответственный флоу.
// Lazy route — нагрузка только при заходе.
// Реальные вызовы account.deletion.request (152-ФЗ «право на забвение»):
// мягкая деактивация → 30 дней grace → hard wipe через Inngest
// processAccountDeletion. См. rules/compliance.md §3, rules/auth.md §8.
//
// Backend account.deletion.request имеет известный security-MAJOR
// (signedToken===userId вместо RS256 SSO re-auth) — carry-over Phase 7,
// здесь не блокируем UI-флоу. Отмена удаления (restore) на сервере
// требует signedToken из email-ссылки — UI его не имеет, поэтому
// отмена выполняется по ссылке из письма (честное поведение, не фейк).

import { Button, Input, Label } from "@studyhub/ui";
import { toast } from "@studyhub/ui/toast";
import {
	IconAlertTriangle,
	IconCalendarClock,
	IconClock,
	IconMailForward,
	IconTrash,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/settings/alert-dialog";
import { SettingsPageContainer, SettingsPageHeader } from "@/components/settings/page-header";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { useOauthLogin } from "@/hooks/use-oauth-login";
import { useCommerceVisible } from "@/lib/module-availability/commerce";
import { api } from "@/lib/trpc-client";
import { userFacingErrorMessage } from "@/lib/user-facing-error";
import { handleAccountDeletionError } from "./-account-deletion-error";

export const Route = createFileRoute("/settings/danger")({
	component: DangerRoute,
	pendingComponent: PagePendingFallback,
});

/** Grace period — 30 дней (синхронизировано с processAccountDeletion). */
const GRACE_DAYS = 30;

function DangerRoute() {
	const { t, i18n } = useTranslation("settings");
	const utils = api.useUtils();
	const meQuery = api.auth.me.useQuery(undefined, { staleTime: 15_000 });
	const me = meQuery.data;
	const requestMutation = api.account.deletion.request.useMutation();
	const reauth = useOauthLogin({ returnTo: "/settings/danger" });

	const [open, setOpen] = useState(false);
	const [confirmInput, setConfirmInput] = useState("");

	const confirmWord = t("danger.confirmTypeWord");
	const canConfirm = confirmInput === confirmWord;

	useEffect(() => {
		if (reauth.errorCode) toast.error(t("danger.deleteError"));
	}, [reauth.errorCode, t]);

	// Источник истины — реальный deletionRequestedAt из auth.me (не URL-демо).
	const requestedAt = me?.deletionRequestedAt ? new Date(me.deletionRequestedAt) : null;
	const requested = requestedAt !== null;
	const wipeAt = requestedAt ? new Date(requestedAt.getTime() + GRACE_DAYS * 86_400_000) : null;
	const daysRemaining = wipeAt
		? Math.max(0, Math.ceil((wipeAt.getTime() - Date.now()) / 86_400_000))
		: 0;

	const dateFmt = new Intl.DateTimeFormat(i18n.language, {
		dateStyle: "medium",
		timeStyle: "short",
	});
	const dateOnlyFmt = new Intl.DateTimeFormat(i18n.language, {
		dateStyle: "medium",
	});

	function onConfirmDelete() {
		// На сервер всегда литерал "DELETE" (контракт), независимо от локали UI.
		requestMutation.mutate(
			{ typeConfirm: "DELETE" },
			{
				onSuccess: () => {
					setOpen(false);
					setConfirmInput("");
					void utils.auth.me.invalidate();
					toast.success(t("danger.deletionRequested"));
				},
				onError: (err) => {
					handleAccountDeletionError(err, {
						startReauth: reauth.start,
						showError: (error) =>
							toast.error(userFacingErrorMessage(error, t) ?? t("danger.deleteError")),
					});
				},
			},
		);
	}

	return (
		<SettingsPageContainer>
			<SettingsPageHeader
				title={t("danger.title")}
				description={t("danger.subtitle")}
				icon={<IconAlertTriangle stroke={1.75} />}
				category="streak"
			/>

			{requested ? (
				<GracePanel
					requestedAt={requestedAt ? dateFmt.format(requestedAt) : "—"}
					wipeAt={wipeAt ? dateOnlyFmt.format(wipeAt) : "—"}
					daysRemaining={daysRemaining}
				/>
			) : (
				<DeleteIntro loading={meQuery.isLoading} onStart={() => setOpen(true)} />
			)}

			<AlertDialog open={open} onOpenChange={setOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("danger.confirmTitle")}</AlertDialogTitle>
						<AlertDialogDescription>{t("danger.confirmBody")}</AlertDialogDescription>
					</AlertDialogHeader>
					<div className="space-y-1.5">
						<Label htmlFor="confirm-input">{t("danger.confirmInputLabel")}</Label>
						<Input
							id="confirm-input"
							value={confirmInput}
							onChange={(e) => setConfirmInput(e.target.value)}
							placeholder={confirmWord}
							autoComplete="off"
							className="font-mono"
						/>
					</div>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setOpen(false)}>
							{t("actions.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							disabled={!canConfirm || requestMutation.isPending || reauth.pending}
							onClick={onConfirmDelete}
						>
							{t("danger.confirmCta")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</SettingsPageContainer>
	);
}

/**
 * GracePanel — статус запланированного удаления: спокойная панель с
 * обратным отсчётом и инструкцией по отмене через email-ссылку.
 */
function GracePanel({
	requestedAt,
	wipeAt,
	daysRemaining,
}: {
	requestedAt: string;
	wipeAt: string;
	daysRemaining: number;
}) {
	const { t } = useTranslation("settings");

	return (
		<section className="overflow-hidden rounded-[var(--radius-panel)] border border-destructive/30 bg-card shadow-e2">
			<div className="flex items-start gap-3 border-b border-destructive/20 bg-destructive/8 px-5 py-4">
				<div className="flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-tile)] bg-destructive/15 text-destructive [box-shadow:inset_0_1px_0_rgb(255_255_255/0.25)]">
					<IconCalendarClock size={20} stroke={1.75} />
				</div>
				<div className="min-w-0">
					<h3 className="text-base font-semibold text-destructive">{t("danger.graceHeading")}</h3>
					<p className="mt-0.5 text-xs text-muted-foreground">{t("danger.graceCountdownHint")}</p>
				</div>
			</div>

			<div className="grid grid-cols-1 divide-y divide-border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
				<GraceStat
					icon={<IconClock size={16} stroke={1.75} />}
					label={t("danger.graceRemainingLabel")}
					value={t("danger.graceRemaining", { days: daysRemaining })}
					emphasised
				/>
				<GraceStat
					icon={<IconCalendarClock size={16} stroke={1.75} />}
					label={t("danger.graceRequestedLabel")}
					value={requestedAt}
				/>
				<GraceStat
					icon={<IconTrash size={16} stroke={1.75} />}
					label={t("danger.graceWipeLabel")}
					value={wipeAt}
				/>
			</div>

			<div className="space-y-2 px-5 py-4">
				<p className="flex items-start gap-2 text-sm text-foreground">
					<IconMailForward
						size={16}
						stroke={1.75}
						className="mt-0.5 shrink-0 text-muted-foreground"
					/>
					{t("danger.graceRestoreViaEmail")}
				</p>
				<p className="text-xs text-muted-foreground">{t("danger.graceAfterIrreversible")}</p>
			</div>
		</section>
	);
}

function GraceStat({
	icon,
	label,
	value,
	emphasised,
}: {
	icon: ReactNode;
	label: string;
	value: string;
	emphasised?: boolean;
}) {
	return (
		<div className="px-5 py-3.5">
			<div className="flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wider text-muted-foreground [&_svg]:size-3.5">
				{icon}
				{label}
			</div>
			<div
				className={
					emphasised
						? "mt-1 text-base font-semibold tabular-nums text-destructive"
						: "mt-1 text-sm font-medium tabular-nums text-foreground"
				}
			>
				{value}
			</div>
		</div>
	);
}

/**
 * DeleteIntro — спокойное объяснение последствий + единственная
 * destructive-клавиша. Без крика: панель e2, акцент точечный.
 */
function DeleteIntro({ loading, onStart }: { loading: boolean; onStart: () => void }) {
	const { t } = useTranslation("settings");
	const commerceVisible = useCommerceVisible();
	// «Активная подписка не возвращается» (bullet4) — только там, где подписка
	// вообще видна: в приложении из App Store тарифной системы нет (3.1.1).
	const bullets = (
		["bullet1", "bullet2", "bullet3", "bullet4", "bullet5", "bullet6"] as const
	).filter((key) => commerceVisible || key !== "bullet4");

	return (
		<section className="overflow-hidden rounded-[var(--radius-panel)] border border-border bg-card shadow-e2">
			<div className="flex items-start gap-3 border-b border-border px-5 py-4">
				<div className="flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-tile)] bg-destructive/12 text-destructive [box-shadow:inset_0_1px_0_rgb(255_255_255/0.25)]">
					<IconTrash size={20} stroke={1.75} />
				</div>
				<div className="min-w-0">
					<h3 className="text-base font-semibold text-foreground">{t("danger.deleteHeading")}</h3>
					<p className="mt-0.5 text-xs text-muted-foreground">{t("danger.deleteIntro")}</p>
				</div>
			</div>

			<div className="space-y-4 px-5 py-4">
				<div>
					<p className="mb-2 text-sm font-medium text-foreground">{t("danger.whatHappens")}</p>
					<ul className="space-y-2">
						{bullets.map((key) => (
							<li key={key} className="flex items-start gap-2.5 text-sm text-muted-foreground">
								<span
									className="mt-1.5 size-1.5 shrink-0 rounded-full bg-destructive/60"
									aria-hidden
								/>
								{t(`danger.${key}`)}
							</li>
						))}
					</ul>
				</div>

				<Button variant="destructive" onClick={onStart} disabled={loading}>
					<IconTrash size={14} stroke={1.75} className="mr-1.5" />
					{t("danger.deleteCta")}
				</Button>
			</div>
		</section>
	);
}
